/**
 * CSRF Token Utility
 * Handles CSRF token extraction and management for session-based authentication
 */

// Get CSRF token from cookies
export const getCSRFToken = (): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

// Set CSRF token in headers
export const setCSRFHeader = (headers: Record<string, string>): void => {
  const csrfToken = getCSRFToken();
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }
};

// Fetch CSRF token from Django (if not in cookies)
export const fetchCSRFToken = async (): Promise<string> => {
  try {
    const response = await fetch('/accounts/login/', {
      credentials: 'include',
    });
    
    // Extract CSRF token from the HTML response
    const html = await response.text();
    const match = html.match(/name="csrfmiddlewaretoken" value="([^"]+)"/);
    
    if (match && match[1]) {
      return match[1];
    }
    
    // Fallback to cookies
    const cookieToken = getCSRFToken();
    if (cookieToken) {
      return cookieToken;
    }
    
    throw new Error('CSRF token not found');
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
};

// Ensure CSRF token is available
export const ensureCSRFToken = async (): Promise<string> => {
  let token = getCSRFToken();
  
  if (!token) {
    token = await fetchCSRFToken();
  }
  
  return token;
};
